/**
 * Prompt for the MRNs to screen. The trial and the facility come from the page's
 * context, so the list of MRNs is the only thing left to ask for.
 */

import { useState } from "react";
import type { ScreeningBatchResponse } from "../../api/clinicalTrialsApiTypes";
import SideModal from "../../components/modals/filterModal/filterModal";

/** Matches the clinical-trials `ANALYZE_MANY_LIMIT` on `POST /{trial_id}/analyze`. */
export const ANALYZE_MRN_LIMIT = 25;

interface AnalyzePatientModalProps {
  /** Whether the prompt is showing. */
  isOpen: boolean;
  /** Trial the patients will be screened against, shown for confirmation. */
  trialLabel: string;
  /** Closes the prompt. */
  onClose: () => void;
  /** Starts screening for the entered MRNs. */
  onAnalyze: (mrns: string[]) => Promise<ScreeningBatchResponse>;
}

/**
 * Split a comma-separated MRN field into a distinct, trimmed list.
 *
 * @param value - Raw input, for example "111, 222, 111"
 * @returns MRNs in first-seen order, with blanks and duplicates removed
 */
export function parseAnalyzeMrns(value: string): string[] {
  const seen = new Set<string>();
  const mrns: string[] = [];
  for (const part of value.split(",")) {
    const mrn = part.trim();
    if (!mrn || seen.has(mrn)) continue;
    seen.add(mrn);
    mrns.push(mrn);
  }
  return mrns;
}

/**
 * One sentence naming patients the batch did not queue.
 *
 * @param result - Accounting returned by the analyze-many route
 * @returns Notice text, or null when every submitted MRN was queued
 */
function leftOutNotice(result: ScreeningBatchResponse): string | null {
  const parts: string[] = [];
  const add = (label: string, mrns: string[] | undefined) => {
    if (mrns?.length) parts.push(`${label}: ${mrns.join(", ")}`);
  };
  add("already screening", result.already_in_flight);
  add("blacklisted", result.blacklisted);
  add("recruited", result.recruited);
  const failed = Object.keys(result.publish_failed ?? {});
  if (failed.length) parts.push(`not queued: ${failed.join(", ")}`);
  if (!parts.length) return null;
  return `Queued ${result.queued} of ${result.total_in_csv}. ${parts.join(". ")}.`;
}

/**
 * Readable message from a failed analyze request.
 *
 * @param error - Thrown by the API client or the page
 * @returns Text to show in the prompt
 */
function analyzeErrorMessage(error: unknown): string {
  if (error && typeof error === "object" && "body" in error) {
    const body = (error as { body?: unknown }).body;
    if (body && typeof body === "object" && "detail" in body) {
      const detail = (body as { detail?: unknown }).detail;
      if (typeof detail === "string" && detail.trim()) return detail;
    }
  }
  if (error instanceof Error && error.message.trim()) return error.message;
  return "Could not start screening";
}

/**
 * Ask for one or more comma-separated MRNs and start their screening.
 *
 * @param props - {@link AnalyzePatientModalProps}
 * @returns The analyze prompt
 */
export function AnalyzePatientModal({
  isOpen,
  trialLabel,
  onClose,
  onAnalyze,
}: AnalyzePatientModalProps) {
  const [mrn, setMrn] = useState("");
  const [isStarting, setIsStarting] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const mrns = parseAnalyzeMrns(mrn);
  const overLimit = mrns.length > ANALYZE_MRN_LIMIT;

  /** Trigger the screening, then close when every MRN was queued. */
  const startScreening = async () => {
    if (!mrns.length || overLimit || isStarting) return;
    setIsStarting(true);
    setNotice(null);
    try {
      const result = await onAnalyze(mrns);
      const leftOut = leftOutNotice(result);
      if (leftOut) {
        setNotice(leftOut);
        return;
      }
      setMrn("");
      onClose();
    } catch (error) {
      setNotice(analyzeErrorMessage(error));
    } finally {
      setIsStarting(false);
    }
  };

  return (
    <SideModal isOpen={isOpen} onClose={onClose} id="analyze-patient-modal">
      <div className="flex h-full flex-col gap-4 p-4">
        <span className="text-base font-semibold text-primaryGray-1">
          Analyze patient
        </span>
        <span className="text-sm text-primaryGray-4">
          Screens these patients against {trialLabel}. Separate MRNs with
          commas. At most {ANALYZE_MRN_LIMIT}.
        </span>
        <label className="text-sm text-primaryGray-4" htmlFor="analyze-mrn">
          MRNs
        </label>
        <textarea
          id="analyze-mrn"
          value={mrn}
          rows={3}
          placeholder="111, 222, 333"
          onChange={(event) => {
            setMrn(event.target.value);
            setNotice(null);
          }}
          className="rounded-md border px-3 py-2 text-sm outline-none"
          style={{
            backgroundColor: "var(--color-input-bg)",
            borderColor: "var(--color-input-border)",
            color: "var(--color-text-primary)",
          }}
        />
        {overLimit && (
          <p className="text-sm text-red-700">
            Enter at most {ANALYZE_MRN_LIMIT} MRNs.
          </p>
        )}
        {notice && <p className="text-sm text-primaryGray-1">{notice}</p>}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={startScreening}
            disabled={!mrns.length || overLimit || isStarting}
            className="rounded-md px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
            style={{ backgroundColor: "#0056D6" }}
          >
            {isStarting ? "Starting…" : "Start screening"}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border px-3 py-2 text-sm"
            style={{ borderColor: "#D0D5DD" }}
          >
            Cancel
          </button>
        </div>
      </div>
    </SideModal>
  );
}

export default AnalyzePatientModal;
